/**
 * Working Hours Utility Functions
 * Handles time zone validation for chat functionality
 * Client can only chat when support is within their working hours
 */

interface WorkingHours {
  startTime: string; // HH:MM format
  endTime: string; // HH:MM format
  timezone: string;
}

// Default fallback working hours (used when API fails)
const DEFAULT_WORKING_HOURS: WorkingHours = {
  startTime: '09:00',
  endTime: '17:00',
  timezone: 'Asia/Karachi',
};

const CLIENT_TIMEZONE = 'Europe/Amsterdam';

/**
 * Converts time string (HH:MM) to minutes since midnight
 */
const timeToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * Converts minutes since midnight back to HH:MM format
 */
// const minutesToTime = (minutes: number): string => {
//   const hours = Math.floor(minutes / 60);
//   const mins = minutes % 60;
//   return `${hours.toString().padStart(2, '0')}:${mins
//     .toString()
//     .padStart(2, '0')}`;
// };

/**
 * Gets the current time in a specific timezone as minutes since midnight
 */
const getCurrentTimeInTimezone = (timezone: string): number => {
  const now = new Date();
  const timeInTimezone = new Date(
    now.toLocaleString('en-US', { timeZone: timezone })
  );
  return timeInTimezone.getHours() * 60 + timeInTimezone.getMinutes();
};

/**
 * Checks if current time in client timezone overlaps with support working hours
 */
export const isWithinWorkingHoursOverlap = (
  workingHours?: WorkingHours
): boolean => {
  try {
    const supportWorkingHours = workingHours || DEFAULT_WORKING_HOURS;

    // Get current time in support timezone
    const supportCurrentTime = getCurrentTimeInTimezone(
      supportWorkingHours.timezone
    );

    // Convert support working hours to minutes
    const supportStart = timeToMinutes(supportWorkingHours.startTime);
    const supportEnd = timeToMinutes(supportWorkingHours.endTime);

    // Check if support is currently in working hours
    const isSupportInWorkingHours =
      supportCurrentTime >= supportStart && supportCurrentTime <= supportEnd;

    return isSupportInWorkingHours;
  } catch (error) {
    console.error('Error checking working hours overlap:', error);
    // If there's an error, allow chat (fail-safe approach)
    return true;
  }
};

/**
 * Gets the next available working hours message for the client
 */
export const getNextWorkingHoursMessage = (
  workingHours?: WorkingHours
): string => {
  try {
    const supportWorkingHours = workingHours || DEFAULT_WORKING_HOURS;
    const now = new Date();

    // Get current time in support timezone
    const supportCurrentTime = getCurrentTimeInTimezone(
      supportWorkingHours.timezone
    );
    const supportStart = timeToMinutes(supportWorkingHours.startTime);
    const supportEnd = timeToMinutes(supportWorkingHours.endTime);

    // Calculate time until next working hours start
    let minutesUntilWorkingHours = 0;

    if (supportCurrentTime < supportStart) {
      // Before working hours today
      minutesUntilWorkingHours = supportStart - supportCurrentTime;
    } else if (supportCurrentTime > supportEnd) {
      // After working hours today, wait until tomorrow
      minutesUntilWorkingHours = 24 * 60 - supportCurrentTime + supportStart;
    }

    // Calculate the next working hours start time in client timezone
    const nextWorkingHoursStart = new Date(
      now.getTime() + minutesUntilWorkingHours * 60 * 1000
    );
    const clientTimeString = nextWorkingHoursStart.toLocaleString('en-US', {
      timeZone: CLIENT_TIMEZONE,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      weekday: 'long',
      month: 'short',
      day: 'numeric',
    });

    return `A support agent will connect to you during their working hours (${supportWorkingHours.startTime} - ${supportWorkingHours.endTime} ${supportWorkingHours.timezone}). Next available time in your timezone: ${clientTimeString}`;
  } catch (error) {
    console.error('Error calculating next working hours:', error);
    const supportWorkingHours = workingHours || DEFAULT_WORKING_HOURS;
    return `A support agent will connect to you during their working hours (${supportWorkingHours.startTime} - ${supportWorkingHours.endTime} ${supportWorkingHours.timezone}).`;
  }
};

/**
 * Gets current working hours status and information
 */
export const getWorkingHoursStatus = (workingHours?: WorkingHours) => {
  const supportWorkingHours = workingHours || DEFAULT_WORKING_HOURS;
  const isWithinHours = isWithinWorkingHoursOverlap(supportWorkingHours);
  const nextHoursMessage = getNextWorkingHoursMessage(supportWorkingHours);

  return {
    isWithinWorkingHours: isWithinHours,
    nextWorkingHoursMessage: nextHoursMessage,
    supportWorkingHours,
    clientTimezone: CLIENT_TIMEZONE,
  };
};

// Export the WorkingHours interface so other components can use it
export type { WorkingHours };
